import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { WeekNav } from './WeekNav';

type ExistingPeriod = RouterOutputs['shadowTimesheets']['listByUserProject'][number];

interface ShadowSidebarProps {
  selectedUserId: string;
  onUserChange: (id: string) => void;
  selectedProjectId: string;
  onProjectChange: (id: string) => void;
  view: 'week' | 'month';
  onViewChange: (v: 'week' | 'month') => void;
  periodLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  showToday: boolean;
  existingPeriods: ExistingPeriod[];
  currentPeriodFrom: string;
  onJumpToPeriod: (period_from: string, period_to: string) => void;
}

function fmtPeriod(from: string, to: string): string {
  const f = from.slice(5).replace('-', '/');
  const t = to.slice(5).replace('-', '/');
  return `${f}–${t}`;
}

export function ShadowSidebar({
  selectedUserId,
  onUserChange,
  selectedProjectId,
  onProjectChange,
  view,
  onViewChange,
  periodLabel,
  onPrev,
  onNext,
  onToday,
  showToday,
  existingPeriods,
  currentPeriodFrom,
  onJumpToPeriod,
}: ShadowSidebarProps) {
  const { data: employees = [] } = trpc.shadowTimesheets.listEmployees.useQuery();
  const { data: projects = [] } = trpc.projects.list.useQuery();

  return (
    <div className="flex w-64 shrink-0 flex-col border-r">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold">Shadow Timesheets</p>
      </div>

      <div className="flex flex-col gap-3 border-b p-4">
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Employee</p>
          <Select value={selectedUserId} onValueChange={onUserChange}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select employee…" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Project</p>
          <Select value={selectedProjectId} onValueChange={onProjectChange}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select project…" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Period</p>
          <div className="flex items-center gap-1 rounded-md border p-1">
            {(['week', 'month'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => onViewChange(tab)}
                className={`flex-1 rounded py-0.5 text-xs font-medium capitalize transition-colors ${
                  view === tab
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <WeekNav
            label={periodLabel}
            onPrev={onPrev}
            onNext={onNext}
            onToday={onToday}
            showToday={showToday}
            todayLabel={view === 'week' ? 'This week' : 'This month'}
          />
        </div>
      </div>

      {existingPeriods.length > 0 && (
        <div className="flex-1 overflow-y-auto p-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Existing shadows</p>
          <div className="space-y-1">
            {existingPeriods.map((p) => {
              const isActive = p.period_from === currentPeriodFrom;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onJumpToPeriod(p.period_from, p.period_to)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-xs transition-colors hover:bg-accent ${
                    isActive ? 'border-primary bg-accent' : ''
                  }`}
                >
                  <div className="font-medium">{fmtPeriod(p.period_from, p.period_to)}</div>
                  <div className="text-muted-foreground">{p.entry_count} entries</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
